// NavBar — top navigation voor v2 SPA (H9 PR-2).
//
// Verantwoordelijkheden:
// - 3 routes: home / settings / tech
// - Site-label rechts naast brand
// - Logout-knop rechts
// - Mobile hamburger-dropdown (< 720px)
// - Active-route visueel highlight (bold + accent-underline)
//
// Stijlen in nav.css. Geen interne data-fetches — alle props ingegoten.

import { useEffect, useState } from 'react';
import './nav.css';

export type Route = 'home' | 'settings' | 'tech';

interface NavBarProps {
  current: Route;
  onNavigate: (r: Route) => void;
  siteLabel?: string;
  onLogout?: () => void;
  /**
   * v36 (2026-07-25): rol-gate — non-admin users zien alleen de Home-link.
   * Settings/Techniek zijn verborgen; App.tsx forceert route back to home
   * bij directe URL-navigatie voor extra veiligheid.
   *
   * De default is bewust `false` en niet `true`. Hij stond op `true`, en één
   * van de twee render-plekken in App.tsx gaf de prop niet mee — dan won de
   * default en zag een bewoner de admin-links staan, die de route-guard hem
   * vervolgens weer afnam. Fail-closed: wie de rol niet meegeeft, krijgt de
   * beperkte balk.
   */
  isAdmin?: boolean;
}

const ALL_LINKS: Array<{ route: Route; label: string; adminOnly: boolean }> = [
  { route: 'home',     label: 'Home',         adminOnly: false },
  { route: 'settings', label: 'Instellingen', adminOnly: true  },
  { route: 'tech',     label: 'Techniek',     adminOnly: true  },
];

export function NavBar({ current, onNavigate, siteLabel, onLogout, isAdmin = false }: NavBarProps) {
  const links = ALL_LINKS.filter((l) => isAdmin || !l.adminOnly);
  const [open, setOpen] = useState(false);

  // Mobile: bij route-wissel automatisch dropdown sluiten zodat de gebruiker
  // direct de page ziet zonder eerst het menu te moeten dismissen.
  useEffect(() => { setOpen(false); }, [current]);

  // Klik buiten de nav → menu sluiten (mobile UX). Listener pas registreren
  // als 'm open is, anders elke render kosten van een document-listener.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const tgt = e.target as HTMLElement | null;
      if (!tgt?.closest('.nav')) setOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  return (
    <nav className="nav" aria-label="Primair">
      <span className="nav__brand">
        ZBS EMS
        {siteLabel && <span className="nav__site">· {siteLabel}</span>}
      </span>

      <button
        type="button"
        className="nav__hamburger"
        aria-expanded={open}
        aria-controls="nav-links"
        aria-label={open ? 'Menu sluiten' : 'Menu openen'}
        onClick={() => setOpen((v) => !v)}
      >
        ☰
      </button>

      <div id="nav-links" className={`nav__links${open ? ' is-open' : ''}`}>
        {links.map(({ route, label }) => (
          <button
            type="button"
            key={route}
            className={`nav__link${current === route ? ' is-active' : ''}`}
            aria-current={current === route ? 'page' : undefined}
            onClick={() => onNavigate(route)}
          >
            {label}
          </button>
        ))}
        {onLogout && (
          <button type="button" className="nav__logout" onClick={onLogout}>
            Uitloggen
          </button>
        )}
      </div>
    </nav>
  );
}
